import { CONSENT_KEY, resolveConsentState } from '@/consent';
import { RecorderError, RecorderErrorCode } from '@/errors';
import { logger } from '@/logger';
import { createRecorder } from '@/Recorder';
import type { RecorderConfig, RecorderStatus, ConsentState } from '@/types';

let activeRecorder: ReturnType<typeof createRecorder> | null = null;
let _initPromise: Promise<string> | null = null;

/**
 * Initializes the CobrowsingUI session recorder.
 *
 * @param config - Configuration options for the recorder
 * @returns
 *   - With `autoStart: true` (default) and consent granted → resolves to the
 *     newly-created session id.
 *   - With `autoStart: false` → resolves with `void`; caller must call
 *     `startRecording()` after consent.
 *   - With `autoStart: true` but the visitor has opted out (DNT, GPC,
 *     `__chatneerOptOut`, or `data-chatneer-no-record`) → resolves with `''`
 *     instead of rejecting, so host pages don't have to try/catch the
 *     normal opt-out path. Other errors (network, config) still reject.
 * @throws Error if apiUrl or apiKey is missing/invalid
 *
 * @example
 * ```typescript
 * import { init, startRecording } from 'cobrowsing-ui';
 *
 * // For support use case - user explicitly starts recording
 * await init({
 *   apiUrl: 'https://api.chatneer.com',
 *   apiKey: 'cnr_xxx',
 *   autoStart: false  // Don't auto-start
 * });
 *
 * // Later, when user clicks "Start Recording"
 * const sessionId = await startRecording();
 * ```
 *
 * @example
 * ```typescript
 * // Auto-start mode (default, backward compatible)
 * const sessionId = await init({
 *   apiUrl: 'https://api.chatneer.com',
 *   apiKey: 'cnr_xxx',
 *   autoStart: true
 * });
 * ```
 */
export function init(config: RecorderConfig): Promise<string | void> {
  if (_initPromise) return _initPromise as Promise<string | void>;

  activeRecorder = createRecorder(config);

  // Persist an explicit consent decision passed through config, before the
  // recorder reads consent on start. A stored decision overrides DNT/GPC, so
  // `consent: 'granted'` is the one-call equivalent of calling
  // `setConsent('granted')` immediately before `init()`.
  if (config.consent) {
    setConsent(config.consent);
  }

  if (config.autoStart === false) {
    return Promise.resolve();
  }

  // Swallow CONSENT_DENIED quietly — when a visitor sets `__chatneerOptOut`,
  // raises DNT, etc., we want `init()` to resolve gracefully rather than
  // surface an exception every host page would need to try/catch. Other
  // errors (network / config) still propagate.
  //
  // `startRecording()` (not `start()`) so the session id stored in this tab's
  // `sessionStorage` survives across page navigations — every page load
  // reuses the same Session row instead of allocating a fresh one. The
  // recorder validates the stored id with GET /:id/meta first and falls back
  // to a new session on 404.
  _initPromise = activeRecorder
    .startRecording()
    .catch((err) => {
      if (err instanceof RecorderError && err.code === RecorderErrorCode.CONSENT_DENIED) {
        // Resolve gracefully, but leave a breadcrumb — otherwise a host that
        // expects recording sees zero network traffic and no error, with no
        // way to tell the opt-out apart from a broken install.
        logger.info(
          'Recording disabled: visitor has opted out (Do Not Track, Global Privacy Control, ' +
            'window.__chatneerOptOut, or <html data-chatneer-no-record>). ' +
            "To override, run: CobrowsingRecorder.setConsent('granted'); CobrowsingRecorder.startRecording();"
        );
        return '';
      }
      throw err;
    })
    .finally(() => {
      _initPromise = null;
    });
  return _initPromise;
}

/**
 * Explicitly start recording after user consent.
 *
 * Use this when autoStart is set to false and you want the user to
 * explicitly start recording (e.g., support use case).
 *
 * @returns Promise resolving to the session ID
 * @throws Error if consent denied or already recording
 *
 * @example
 * ```typescript
 * import { init, startRecording, stopRecording, getConsent, setConsent } from 'cobrowsing-ui';
 *
 * // Initialize without auto-start
 * await init({ apiUrl, apiKey, autoStart: false });
 *
 * // Check consent state
 * if (getConsent() === 'pending') {
 *   // Show your own consent modal
 * }
 *
 * // After user accepts
 * setConsent('granted');
 * const sessionId = await startRecording();
 *
 * // When user wants to stop
 * await stopRecording();
 * ```
 */
export async function startRecording(): Promise<string> {
  if (!activeRecorder) {
    throw new Error('[CobrowsingUI] Recorder not initialized. Call init() first.');
  }
  return activeRecorder.startRecording();
}

/**
 * Explicitly stop recording and end the session.
 *
 * Clears the session from storage so the next startRecording() creates a new session.
 *
 * @returns Promise that resolves when stopping is complete
 *
 * @example
 * ```typescript
 * import { stopRecording } from 'cobrowsing-ui';
 *
 * await stopRecording();
 * console.log('Recording stopped');
 * ```
 */
export async function stopRecording(): Promise<void> {
  if (!activeRecorder) {
    return;
  }
  await activeRecorder.stopRecording();
  activeRecorder = null;
}

/**
 * Returns the consent state the recorder would apply to a *new* session
 * right now. Reflects the same chain `Recorder.start()` consults — stored
 * decision → runtime opt-out signals (DNT, GPC, `__chatneerOptOut`,
 * `data-chatneer-no-record`) → `autoGrantConsent` default — so a host page
 * checking this value before calling `startRecording()` sees a state
 * consistent with what the recorder will gate on.
 *
 * Caveat: if `init()` has been called with `autoGrantConsent: false`, this
 * function (which can't see that config) falls back to the safe default
 * (`autoGrantConsent: true`). The mismatch only matters in the unusual
 * "no consent stored AND no opt-out signal AND autoGrantConsent disabled"
 * window. Pass the same config to both call sites if that case is reachable.
 *
 * @returns 'pending' | 'granted' | 'denied'
 *
 * @example
 * ```typescript
 * import { getConsent } from 'cobrowsing-ui';
 *
 * const state = getConsent();
 * // 'pending' - no stored decision, no opt-out signal, autoGrantConsent disabled
 * // 'granted' - explicitly granted, or auto-granted by default
 * // 'denied'  - explicitly denied, or any opt-out signal active
 * ```
 */
export function getConsent(): ConsentState {
  return resolveConsentState();
}

/**
 * Sets the consent state for recording.
 *
 * @param state - 'granted' to allow recording, 'denied' to block it
 *
 * @example
 * ```typescript
 * import { setConsent, startRecording } from 'cobrowsing-ui';
 *
 * // After user accepts in your consent modal
 * setConsent('granted');
 * await startRecording();
 * ```
 *
 * @example
 * ```typescript
 * // After user declines
 * setConsent('denied');
 * // startRecording() will now throw an error
 * ```
 */
export function setConsent(state: 'granted' | 'denied'): void {
  try {
    sessionStorage.setItem(CONSENT_KEY, state);
  } catch {
    // Ignore - private browsing mode
  }
}

/**
 * Bridges the recorder's visitor id onto a caller-supplied identifier — most
 * commonly the visitor's id from the chat widget. The supplied id replaces
 * the recorder's effective `Session.userId` on every **subsequent** session
 * create and is persisted to `localStorage` so the next page load keeps it
 * without the host page having to push it again.
 *
 * The currently-running session (if any) is *not* retroactively updated —
 * once chat resolves the visitor, the next session create picks up the new
 * id. This usually means "the visitor's next page load" since recorders
 * don't normally roll mid-session.
 *
 * Pass `''` / `null` to clear the override; the next session will generate a
 * fresh anonymous `cnv_<uuid>`. No-op if `init()` was never called.
 *
 * @example
 * ```typescript
 * import { init, setVisitorId } from 'cobrowsing-ui';
 *
 * await init({ apiUrl: 'https://...', apiKey: 'cnr_...' });
 *
 * // Later — once the chat widget resolves the visitor:
 * window.SB?.on?.('user:identified', (sbUserId) => {
 *   setVisitorId(sbUserId);
 * });
 * ```
 */
export function setVisitorId(userId: string | null | undefined): void {
  activeRecorder?.setVisitorId(userId);
}

/**
 * Gets the current status of the recorder.
 *
 * @returns RecorderStatus object with sessionId and state
 *
 * @example
 * ```typescript
 * import { getState } from 'cobrowsing-ui';
 *
 * const status = getState();
 * console.log(status.state);    // 'idle' | 'recording' | 'stopped' | 'error'
 * console.log(status.sessionId); // 'abc-123' or ''
 * ```
 */
export function getState(): RecorderStatus {
  return activeRecorder?.getStatus() ?? { sessionId: '', state: 'idle' };
}

/**
 * Emits a custom event that will be attached to the session recording.
 * Useful for tracking user actions like button clicks, form submissions, etc.
 *
 * @param tag - Identifier for the custom event type
 * @param payload - Data to attach to the event
 *
 * @example
 * ```typescript
 * import { emitCustomEvent } from 'cobrowsing-ui';
 *
 * // Track a button click
 * emitCustomEvent('button-click', { buttonId: 'submit-form', text: 'Submit' });
 *
 * // Track a form submission
 * emitCustomEvent('form-submit', { formId: 'contact', fields: { name: 'John' } });
 * ```
 */
export function emitCustomEvent(tag: string, payload: unknown): void {
  activeRecorder?.addCustomEvent(tag, payload);
}

/**
 * Subscribes to agent-cursor updates from the live cobrowsing channel.
 * Fires whenever the agent moves their cursor over the live session viewer,
 * with `x`/`y` already converted from the agent-side normalised coordinates
 * to pixel coordinates in the customer's page.
 *
 * The SDK renders a ghost cursor overlay automatically — this callback is
 * only needed for custom behaviour on top (highlighting an element, showing
 * a tooltip, etc.). No-op when `init()` hasn't been called or when the live
 * transport isn't carrying cursor data (e.g. Pusher / REST-only mode).
 *
 * @returns an `unsubscribe` function that clears the registered callback.
 *
 * @example
 * ```typescript
 * import { onAgentCursor } from 'cobrowsing-ui';
 *
 * const off = onAgentCursor((x, y) => {
 *   console.log('Agent is at', x, y);
 * });
 *
 * // Later, when no longer needed:
 * off();
 * ```
 */
export function onAgentCursor(callback: (x: number, y: number) => void): () => void {
  return activeRecorder?.onAgentCursor(callback) ?? (() => {});
}

/**
 * Host-page handler for the agent's "may I interact?" request. Returns
 * `'granted'` or `'denied'`. When no handler is registered, the SDK shows
 * its default consent modal.
 *
 * @example
 * ```typescript
 * onControlRequest(async ({ agentName, brandName }) => {
 *   const ok = await myCustomConsentBanner({ agentName, brandName });
 *   return ok ? 'granted' : 'denied';
 * });
 * ```
 *
 * @returns an `unsubscribe` function that clears the registered handler.
 */
export function onControlRequest(
  handler: (info: { agentName: string; brandName: string }) => Promise<'granted' | 'denied'>
): () => void {
  return activeRecorder?.setOnControlRequest(handler) ?? (() => {});
}

/**
 * Stops the current recording session and flushes any remaining events.
 *
 * @returns Promise that resolves when stopping is complete
 *
 * @example
 * ```typescript
 * import { stop } from 'cobrowsing-ui';
 *
 * await stop();
 * console.log('Recording stopped');
 * ```
 */
export async function stop(): Promise<void> {
  _initPromise = null;
  if (activeRecorder) {
    await activeRecorder.stop();
  }
  activeRecorder = null;
}

/**
 * Logs out the current user and stops the recorder.
 *
 * This should be called when the user logs out of your application.
 * It stops the recorder, flushes any remaining events, and clears
 * the session from storage so a subsequent login starts a clean session.
 *
 * @returns Promise that resolves when logout is complete
 *
 * @example
 * ```typescript
 * import { logout } from 'cobrowsing-ui';
 *
 * // When user clicks logout
 * await logout();
 * // User can now log in as a different user with a fresh session
 * ```
 */
export async function logout(): Promise<void> {
  await stop();
}

// Re-export types for consumers
export type {
  RecorderConfig,
  MaskInputConfig,
  RecorderStatus,
  RecorderState,
  CobrowsingCustomEvent,
  ConsentState,
  IDomRecorder,
  ITransport,
  IEventStore,
  ISessionClient,
} from '@/types';

// Re-export classes for advanced consumers who want to create custom implementations
export { Recorder, createRecorder } from '@/Recorder';

// Re-export errors for programmatic handling
export { RecorderError, RecorderErrorCode, getErrorCode, ERROR_MESSAGES } from '@/errors';

// Re-export logger for external logging control
export {
  LogLevel,
  setLogLevel,
  getLogLevel,
  setLogCallback,
  createLogger,
  enableDebug,
  disableLogging,
} from '@/logger';
